import { useState } from "react";
import backendUrl from "../../config";
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollLock } from "../../hooks/useScrollLock";
import { useStatusReset } from "../../hooks/useStatusReset";

export default function EmailPopup({ onClose }) {
    const [email, setEmail] = useState(""); // State to store the email input
    const [status, setStatus] = useState(""); // State to store success/error messages
    const [statusType, setStatusType] = useState("");
    const [loading, setLoading] = useState(false);
    
    const BACKENDURL = backendUrl;
    // const BACKENDURL = "http://localhost:5000"

    // Lock scroll when popup is open
    useScrollLock(true);

    // Reset status after 2s
    useStatusReset(status, setStatus, 2000)

    const handleSubscribe = async () => {
        if (!email) {
            setStatus('Please enter a valid email.');
            setStatusType("error");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${BACKENDURL}/subscribe/email-signup`, {email});
            if (response.status === 200) {
                setStatus("Thank you for subscribing!");
                setStatusType("success");
                setEmail("");
                localStorage.setItem("emailPopupSignedUp", "true");
                // two seconds before the popup page closes.
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setStatus(response.data.error || "Failed to subscribe. Please try again");
                setStatusType("error");
            }
        } catch (error) {
            if (axios.isAxiosError(error)){
                // console.error("Error subscribing: ", error); // removed for production security reason
                setStatus(error.response?.data?.error || "An error occurred. Please try again later.");
                setStatusType("error");
            }else{
                setStatus("An unexpected error occurred. Please try again later.");
            }
            
        } finally {
            setLoading(false); // Stop loading when subscription is complete
        }
    }

    // to close the popup message and setup cooldown period until popup the message
    const handleClose = () => {
        localStorage.setItem("emailPopupLastShown", Date.now());
        onClose();
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 w-full h-full">
            {/* Clicking black background allow you to close the popup page */}
            <div className="fixed z-40 flex items-center justify-center bg-black bg-opacity-50 w-full h-full"
                onClick={handleClose}
            />

            {/* main content */}
            <div className="fixed relative z-50 flex flex-col items-center justify-center bg-white w-[80%] h-[80%] gap-y-[2rem] rounded-lg shadow-lg px-[1rem]">
                {/* Close button */}
                <img
                    src="/pictures/main_logo.webp"
                    alt="Takamori Robot Logo"
                />
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 text-gray-600 hover:text-black"
                >
                    ✕
                </button>

                <h2 className="text-center text-3xl font-bold text-[#00B1F5]">Stay up-to-date with us by joining our mailing list!</h2>
                <p className="text-center text-xl">Sign up to get updates, exclusive offers, and learn about our kits!</p>

                <form
                    className="flex flex-col w-[90%] lg:w-[60%] items-center justify-center"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubscribe();
                    }}
                >
                    <div className="w-full lg:w-[60%] shadow-2xl hover:shadow-[#249CFF] mb-[2rem]">
                        <input 
                            className="w-full h-10 px-3 text-sm text-gray-700 border border-r rounded-r-none border-blue-500 focus:outline-none rounded" 
                            type="text" 
                            placeholder="example@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="group relative flex flex-row justify-center items-center before:ease relative h-12 w-36 overflow-hidden border text-black shadow-2xl transition-all before:absolute before:top-1/2 before:h-0 before:w-64 before:origin-center before:-translate-x-20 before:rotate-45 before:bg-blue-500 before:duration-300 hover:text-white hover:shadow-[#249CFF] hover:before:h-64 hover:before:-translate-y-32">
                        <button
                            id="submit-button"
                            onClick={handleSubscribe}
                            disabled={loading}
                            className="group relative w-full h-full flex items-center justify-center"
                        >
                            {loading ? (
                                <div className="relative group w-5 h-5 border-2 border-[#249CFF] border-t-white rounded-full animate-spin" />
                            ) : (
                                <div className="relative text-[#249CFF] z-10 group-hover:text-white transition duration-200">
                                    SUBSCRIBE
                                </div>
                            )}
                        </button>
                    </div>
                    <AnimatePresence>
                        {status && (
                            <motion.div
                                key="status-message"
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 50 }}
                                transition={{ duration: 1 }}
                                className={`absolute bottom-[5%] -translate-x-1/2 mt-2 w-[80%] md:w-[50%]
                                    flex items-center justify-center gap-3 px-4 py-3 rounded-lg text-sm font-medium shadow-md
                                    ${statusType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                            >
                                <div className="flex items-center justify-center w-5 h-5">
                                    {statusType === 'success' ? (
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    ) : (
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    )}
                                </div>
                            <p>{status}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </div>
        </div>
    );
}
